import { useState } from 'react';

import { Flex, HStack, Text } from '@chakra-ui/react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Deal, DealStateEnum } from '../../gql/dealGql';
import {
  DealType,
  NewEstimateCustomerInput,
  NewEstimateUpsertInput,
  useDuplicateDealsLazyQuery,
  useNewEstimateUpsertMutation,
} from '../../gql/generated/graphql';

import LeaseEndContainer from '../../components/Container/LEContainer';
import EstimateForm from '../../components/EstimateForm/EstimateForm';

import { getPreFilledEstimate } from './utils';

import ROUTES from '../../constants/routes';
import useGenerateBookSheets from '../../hooks/useGenerateBookSheets';
import { useNewEstimateContext } from '../../hooks/useNewEstimateContext';
import { useUser } from '../../hooks/useUser';
import { logger } from '../../libs/Logger';
import { getTimezoneStr } from '../../libs/utils';
import { cleanDealForUpdate } from '../../utils/deals';

const NewEstimate = () => {
  const location = useLocation();
  const [setHandleSubmitting, setSetHandleSubmitting] = useState<(isSubmitting: boolean) => void>(
    (_: boolean) => _,
  );
  const history = useHistory();
  if (location.search) {
    const removeQueryParamsFromUrl = () => history.replace(ROUTES.NEW_ESTIMATE);
    removeQueryParamsFromUrl();
  }
  const { id } = useUser();
  const { duplicateDealsModal, setDuplicateDeals } = useNewEstimateContext();
  const { generateBookSheets } = useGenerateBookSheets();

  const preFilledEstimate = getPreFilledEstimate(location);

  const [creationDate] = useState(new Date());

  const [upsertEstimate, { loading: upsertEstimateLoading }] = useNewEstimateUpsertMutation();
  const [checkDuplicateDeals] = useDuplicateDealsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const submitEstimate = async (estimate: Deal) => {
    const newDeal = cleanDealForUpdate(estimate);
    const dealState = newDeal.state;

    const partialCustomer: NewEstimateCustomerInput = {
      address: newDeal.customer.address,
      dl_expiration_date: newDeal.customer.dl_expiration_date,
      dob: newDeal.customer.dob,
      email: newDeal.customer.email,
      employment: newDeal.customer.employment,
      first_name: newDeal.customer.first_name,
      has_full_ssn: newDeal.customer.has_full_ssn,
      lives_with_other_buyer: newDeal.customer.lives_with_other_buyer,
      home_phone_number: newDeal.customer.home_phone_number,
      id: newDeal.customer.id,
      last_name: newDeal.customer.last_name,
      marital_status: newDeal.customer.marital_status,
      middle_name: newDeal.customer.middle_name,
      name_suffix: newDeal.customer.name_suffix,
      phone_number: newDeal.customer.phone_number,
      relation_to_buyer: newDeal.customer.relation_to_buyer,
      has_itin: newDeal.customer.has_itin,
      ssn_id: newDeal.customer.ssn_id,
    };

    const partialPayoff = newDeal.car.payoff;
    delete partialPayoff?.doubleTaxApplied;
    delete partialPayoff?.previousTotalPayoff;
    delete partialPayoff?.totalPayoff;

    const partialFinancialInfo = newDeal.financial_info;
    delete partialFinancialInfo?.totalTaxAmount;

    const car = {
      ...newDeal.car,
      payoff: partialPayoff,
    };

    try {
      const newEstimateUpsertInput: NewEstimateUpsertInput = {
        id: newDeal.id,
        type: newDeal.type as unknown as DealType,
        country: estimate.country,
        request_boot: newDeal.request_boot,
        state: dealState,
        setter_id: newDeal.setter_id,
        customer: partialCustomer,
        financial_info: partialFinancialInfo,
        source: newDeal.source,
        needs_electronic_signature_verification: newDeal.needs_electronic_signature_verification,
        car,
        creation_date_utc: creationDate,
        creation_date_tz: getTimezoneStr(),
        notes: [
          ...newDeal.notes,
          {
            text: 'Deal Created',
            author_id: id,
            creation_date_tz: getTimezoneStr(),
          },
        ],
        referral_source: newDeal.referral_source?.source_name ? newDeal.referral_source : null,
      };

      if (newDeal.selected_dealer_id) {
        (newEstimateUpsertInput as { selected_dealer_id?: number }).selected_dealer_id =
          newDeal.selected_dealer_id;
      }

      const res = await upsertEstimate({
        variables: {
          newEstimateUpsertInput,
        },
      }).catch((e: unknown) => {
        toast.error(e instanceof Error && e.message ? e.message : 'Error creating estimate.');
        throw e;
      });

      const { state = '', customer, id: dealId, country } = res?.data?.newEstimateUpsert ?? {};

      if (state === DealStateEnum.SoftClose || state === DealStateEnum.Floor) {
        await generateBookSheets({ dealId, customerState: customer?.address?.state, country });
      }

      history.push(
        dealId && (state === DealStateEnum.SoftClose || state === DealStateEnum.Finalized)
          ? `/deals/${dealId}`
          : ROUTES.DASHBOARD,
      );
    } catch (e) {
      logger.error('NewEstimate.tsx', '', null, e);
    } finally {
      setHandleSubmitting(false);
    }
  };

  const handleSubmit = async (deal: Deal, newSetSubmitting: (isSubmitting: boolean) => void) => {
    setSetHandleSubmitting(() => newSetSubmitting);

    const { data, error } = await checkDuplicateDeals({
      variables: {
        firstName: deal.customer.first_name,
        lastName: deal.customer.last_name,
        phoneNumber: deal.customer.phone_number,
        homePhoneNumber: deal.customer.home_phone_number,
        email: deal.customer.email,
        vin: deal.car.vin,
        matchBootedDeals: true,
      },
    });

    if (error) {
      newSetSubmitting(false);
      return;
    }
    if (setDuplicateDeals && duplicateDealsModal && data && data.duplicateDeals?.length) {
      setDuplicateDeals(data?.duplicateDeals as Deal[]);
      duplicateDealsModal.onOpen();
    } else {
      await submitEstimate(deal);
    }
    newSetSubmitting(false);
  };

  return (
    <LeaseEndContainer>
      <Flex flexDirection="column" justify="center" maxW="1000px" mx="auto">
        <HStack p={2} bgColor="oxfordBlue" borderTopRadius={8} spacing={0}>
          <Text pl={2} fontSize={18} textColor="white">
            New Estimate
          </Text>
        </HStack>

        <EstimateForm
          estimate={preFilledEstimate}
          onSave={handleSubmit}
          showNoteField
          submitEstimate={submitEstimate}
          upsertEstimateLoading={upsertEstimateLoading}
        />
      </Flex>
    </LeaseEndContainer>
  );
};
export default NewEstimate;
